import { useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { APPLICATION_STRINGS } from '../../strings'

import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'

import { setSafStatusProgress } from '../ActionCreators'

// TODO(mkramerl): These dialogs are no longer CFD-specific. Move them to a more generic location.
import { VNDialog } from '../../VNCustomerFacingDisplay/components/VNDialog'
import { VNProgressCounter } from '../../VNCustomerFacingDisplay/components/VNProgressCounter'
import ConfirmModalV2 from '../../components/ConfirmModalV2'

export const MODAL_NAMES = {
  NETWORK: 'network',
  SYNC_OFFLINE_MODE: 'syncOfflineMode',
  NO_SYNC: 'noSync',
  CARD_READER_CONNECT: 'cardReaderConnect',
  PROCESSOR_SYNC_FAILED: 'processorSyncFailed',
  STOP_SYNC_REQUEST_MADE: 'stopSyncRequestMade',
  SYNC_COMPLETION: 'syncCompletion',
  SYNCING: 'syncing',
  SYNC_INITIATED: 'syncInitiated',
}

export const VNSyncModalManager = ({
  modalName,
  syncingInProgress,
  safStatusProgress,
  platformSyncCount,
  currentSyncingStep,
  startSyncCycle,
  stopSyncCycle,
  onStopSyncClick,
  onCheckNetworkClick,
  setModalName,
  modalMessageOverride,
}) => {
  const dispatch = useDispatch()

  const isMobileViewPort = useGetIsMobileScreen()

  const syncMessage = isMobileViewPort
    ? 'If you continue, you will be blocked\n from any further actions on this\n device until the sync is complete.\n This process can take several\n minutes.'
    : 'If you continue, you will be blocked\n from any further actions on this device\n until the sync is complete. This\n process can take several minutes.'

  const stopSyncMessage = isMobileViewPort
    ? 'Stopping the sync will leave some orders unsynced.'
    : 'Stopping the sync will leave some\n orders unsynced.'

  const noSyncMessage = 'Nothing to sync'
  const syncOfflineMessage =
    'You can not sync while the card reader is in an offline mode. Please try again when the card reader is online.'

  const insufficientNetworkMessage =
    'Your network connection is\n insufficient. Please find a stable\n network connection and retry syncing\n orders.'

  // START Properties for the main sync modal.

  const syncingButtonText = APPLICATION_STRINGS.CANCEL_SYNC
  const syncingHeaderText = useMemo(() => {
    if (currentSyncingStep === 0) {
      return APPLICATION_STRINGS.CURRENTLY_SYNCING
    }

    if (currentSyncingStep === 1) {
      return APPLICATION_STRINGS.SYNCING_ORDER_DETAILS
    }

    if (currentSyncingStep === 2) {
      return APPLICATION_STRINGS.SYNCING_CARD_READER
    }

    if (currentSyncingStep === 3) {
      return APPLICATION_STRINGS.SYNCING_STATUS
    }
  }, [currentSyncingStep])

  const syncingSubtext = useMemo(() => {
    if (currentSyncingStep === 0) {
      return syncingHeaderText
    }

    if (currentSyncingStep === 1) {
      return `${platformSyncCount} orders left`
    }

    if (currentSyncingStep === 2) {
      return syncingHeaderText
    }

    if (currentSyncingStep === 3 && safStatusProgress > 0) {
      return `${safStatusProgress} orders left`
    }

    if (currentSyncingStep === 3 && safStatusProgress === 0) {
      return 'Checking card reader statuses on unknown states ...'
    }
  }, [currentSyncingStep, syncingHeaderText, platformSyncCount, safStatusProgress])

  if (modalName === MODAL_NAMES.NETWORK) {
    return (
      <ConfirmModalV2
        onButtonOneClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        onButtonTwoClick={() => {
          onCheckNetworkClick()
          setModalName(null)
        }}
        headerText={''}
        subtext={insufficientNetworkMessage}
        buttonOneText={'Cancel Sync'}
        buttonTwoText={'Check Network'}
        buttonsDisabled={false}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.SYNC_OFFLINE_MODE) {
    return (
      <ConfirmModalV2
        onButtonTwoClick={() => {
          setModalName(null)
        }}
        headerText={''}
        subtext={syncOfflineMessage}
        buttonTwoText={'OK'}
        singleButtonOnly={true}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.NO_SYNC) {
    return (
      <ConfirmModalV2
        onButtonTwoClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        headerText={''}
        subtext={noSyncMessage}
        buttonTwoText={'OK'}
        singleButtonOnly={true}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.CARD_READER_CONNECT) {
    return (
      <ConfirmModalV2
        onButtonTwoClick={() => {
          setModalName(null)
          startSyncCycle()
        }}
        onButtonOneClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        headerText={''}
        subtext={'No card reader connection. Try syncing again.'}
        buttonOneText={'CANCEL'}
        buttonTwoText={'RETRY'}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.PROCESSOR_SYNC_FAILED) {
    return (
      <ConfirmModalV2
        onButtonTwoClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        headerText={'Processor Sync Failed'}
        subtext={modalMessageOverride || 'Transaction syncing failed.'}
        singleButtonOnly={true}
        buttonTwoText={'OK'}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.STOP_SYNC_REQUEST_MADE) {
    return (
      <ConfirmModalV2
        onButtonOneClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        onButtonTwoClick={() => {
          setModalName(MODAL_NAMES.SYNCING)
        }}
        headerText={''}
        subtext={stopSyncMessage}
        buttonOneText={'Stop Sync Now'}
        buttonTwoText={'Continue Sync'}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.SYNCING) {
    return (
      <VNDialog
        open={syncingInProgress}
        title={syncingHeaderText}
        subTitle={syncingSubtext}
        image={<VNProgressCounter count={currentSyncingStep} total={3} />}
        showTextfield={false}
        closeOnPrimaryButtonClicked={false}
        secondaryButtonDisabled={true}
        primaryButtonText={syncingButtonText}
        onPrimaryButtonClick={() => {
          onStopSyncClick()
          setModalName(MODAL_NAMES.STOP_SYNC_REQUEST_MADE)
        }}
        fullWidth
        width={isMobileViewPort ? '100%' : undefined}
      />
    )
  }

  if (modalName === MODAL_NAMES.SYNC_INITIATED) {
    return (
      <ConfirmModalV2
        onButtonOneClick={() => {
          stopSyncCycle()
          setModalName(null)
        }}
        onButtonTwoClick={() => {
          startSyncCycle()
        }}
        headerText={''}
        subtext={syncMessage}
        buttonOneText={'Cancel Sync'}
        buttonTwoText={'Continue'}
        alwaysEnableButtons={true}
      />
    )
  }

  if (modalName === MODAL_NAMES.SYNC_COMPLETION) {
    return (
      <ConfirmModalV2
        onButtonTwoClick={() => {
          setModalName(null)
          dispatch(setSafStatusProgress(null))
        }}
        headerText={''}
        subtext={'All orders are now synced.'}
        buttonTwoText={'OK'}
        singleButtonOnly={true}
        alwaysEnableButtons={true}
      />
    )
  }

  return <></>
}
